import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ProvinceClient, ProvinceDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new ProvinceClient(API_BASE_URL, axiosInstance);

// Query hook - Get all provinces for dropdowns (cached forever)
export const useProvinces = () => {
  return useQuery({
    queryKey: ['provinces'],
    queryFn: async () => {
      const response = await apiClient.searchProvinces({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'Title',
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });
};

// Direct API search (no caching) - for admin tables with server-side paging
export const useProvinceSearch = () => {
  const search = async (params: {
    page: number;
    pageSize: number;
    sortBy?: string;
    sortDescending?: boolean;
    filters?: any;
  }) => {
    const response = await apiClient.searchProvinces({
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy || 'CreatedUtc',
      sortDescending: params.sortDescending ?? true,
      filters: params.filters || {},
    });
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };

  return { search };
};

// Query hook - Get single province by ID
export const useProvinceById = (id: string) => {
  return useQuery({
    queryKey: ['provinces', id],
    queryFn: async () => {
      const response = await apiClient.getProvinceById(id);
      return response?.data;
    },
    enabled: !!id,
    staleTime: Infinity,
    refetchOnReconnect: true,
  });
};

// Mutation hook - Create province
export const useCreateProvince = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: ProvinceDTO) => {
      await apiClient.createProvince(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provinces'] });
    },
  });
};

// Mutation hook - Update province
export const useUpdateProvince = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ProvinceDTO }) => {
      await apiClient.updateProvince(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provinces'] });
    },
  });
};

// Mutation hook - Delete province
export const useDeleteProvince = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.deleteProvince(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provinces'] });
    },
  });
};